import router from '@/router'
import axios, {
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
  type AxiosError,
  type AxiosInstance,
} from 'axios'
import {
  getToken,
  getRefreshToken,
  setAuthCookies,
  removeAuthCookies,
  getOrganizationId,
  removeStorageItems,
  isAuthRoute,
  parseJwt,
} from '@/utils/auth'
import { isLoading } from '@/global/state/GlobalState'

const API_BASE_URL = process.env.VUE_APP_API_BASE_URL

// --- State ---
let isRefreshing = false
let failedQueue: { resolve: (value: string | null) => void; reject: (reason?: unknown) => void }[] =
  []

// --- Axios Instance ---
const axiosClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-type': 'application/json',
  },
})

const logout = () => {
  removeAuthCookies()
  removeStorageItems()
  router.push({ name: 'login' })
}

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error)
    } else {
      prom.resolve(token)
    }
  })
  failedQueue = []
}

const refreshAuthToken = async (): Promise<string> => {
  const currentRefreshToken = getRefreshToken()
  if (!currentRefreshToken) throw new Error('No refresh token')

  const response = await axios.post(
    `${API_BASE_URL}/sessions/refresh-token`,
    {},
    {
      headers: { Authorization: `Bearer ${currentRefreshToken}` },
    },
  )

  const { token, refreshToken } = response.data
  setAuthCookies(token, refreshToken)

  return token
}

// Request Interceptor
axiosClient.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  isLoading.value = true
  let token = getToken()
  const refreshTokenExists = getRefreshToken()
  const orgId = getOrganizationId()

  if (orgId) {
    config.headers['X-Organization-Id'] = orgId
  }

  // Proactive refresh logic
  if (
    token &&
    refreshTokenExists &&
    !config.url?.includes('/sessions/refresh-token') &&
    !isAuthRoute(config.url)
  ) {
    const decoded = parseJwt(token)
    if (decoded?.exp) {
      const now = Date.now() / 1000
      // Refresh if expiring in less than 5 minutes
      if (decoded.exp - now < 300) {
        if (!isRefreshing) {
          isRefreshing = true
          try {
            token = await refreshAuthToken()
            processQueue(null, token)
          } catch (err) {
            processQueue(err, null)
          } finally {
            isRefreshing = false
          }
        } else {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject })
          })
            .then((newToken) => {
              if (newToken && typeof newToken === 'string') {
                config.headers.Authorization = `Bearer ${newToken}`
              }
              return config
            })
            .catch((err) =>
              Promise.reject(err),
            ) as Promise<InternalAxiosRequestConfig>
        }
      }
    }
  }

  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }

  return config
})

// Response Interceptor
axiosClient.interceptors.response.use(
  (response) => {
    isLoading.value = false
    return response
  },
  async (error: AxiosError) => {
    isLoading.value = false
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean
    }

    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/sessions/refresh-token') &&
      !isAuthRoute(originalRequest.url)
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        })
          .then((token) => {
            originalRequest.headers.Authorization = 'Bearer ' + token
            return axiosClient(originalRequest)
          })
          .catch((err) => Promise.reject(err))
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        const newToken = await refreshAuthToken()
        processQueue(null, newToken)
        originalRequest.headers.Authorization = 'Bearer ' + newToken
        return axiosClient(originalRequest)
      } catch (err) {
        processQueue(err, null)
        logout()
        return Promise.reject(err)
      } finally {
        isRefreshing = false
      }
    }

    if (error.response?.status === 401 && !isAuthRoute(originalRequest?.url)) {
      logout()
    }

    return Promise.reject(error)
  },
)

export default axiosClient